import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  BadgeIndianRupee,
  BriefcaseBusiness,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  RefreshCcw,
  ShieldCheck,
  Users
} from 'lucide-react';
import { getUserPrivileges, getUserRole, isAuthenticated } from '../../utils/auth';
import api from '../../utils/api';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar/sidebar';
import './Dashboard.css';
import { API_BASE_URL } from '../../utils/apiBase';
import { claimAccessPrivileges, MANAGE_POLICY_PRIVILEGES } from '../../utils/managePolicyPrivileges';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(Number(value || 0));

const formatNumber = (value) =>
  new Intl.NumberFormat('en-IN').format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const Dashboard = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [renewalExpanded, setRenewalExpanded] = useState(false);
  const [summary, setSummary] = useState({
    permissions: {
      canViewRenewals: false,
      canViewClaims: false,
      canViewEmployees: false
    },
    businessSummary: null,
    renewalSummary: null,
    claimsSummary: null,
    employeeSummary: null
  });

  const role = getUserRole();
  const privileges = getUserPrivileges();
  const isAdmin = role === 'admin';
  const hasRenewalAccess =
    isAdmin || privileges.includes(MANAGE_POLICY_PRIVILEGES.renewals.process);
  const hasClaimsAccess = isAdmin || claimAccessPrivileges.some((privilege) => privileges.includes(privilege));

  useEffect(() => {
    let ignore = false;

    const loadDashboard = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await api.get(`${API_BASE_URL}/api/dashboard/summary`);
        if (!ignore) {
          setSummary(response.data);
        }
      } catch (err) {
        if (!ignore) {
          setError(err?.response?.data?.error || 'Unable to load dashboard.');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      ignore = true;
    };
  }, []);

  if (!isAuthenticated()) {
    return <Navigate to="/" />;
  }

  const businessSummary = summary.businessSummary;
  const renewalSummary = summary.renewalSummary;
  const claimsSummary = summary.claimsSummary;
  const employeeSummary = summary.employeeSummary;
  const showRenewalCard = hasRenewalAccess && summary.permissions.canViewRenewals;
  const showClaimsCard = hasClaimsAccess && summary.permissions.canViewClaims;
  const showEmployeeCard = isAdmin && summary.permissions.canViewEmployees;

  return (
    <div className="dashboard-container">
      <div className="sidebar">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>

      <div className={`dashboard-content ${collapsed ? 'collapsed' : ''}`}>
        <div className="dashboard-sticky-header">
          <Header />
        </div>

        <div className="dashboard-scroll-area">
          <section className="dashboard-shell">
            <div className="dashboard-heading">
              <div>
                <p className="dashboard-eyebrow">Dashboard</p>
                <p className="dashboard-subtitle">
                  Monthly business, renewals, claims, and workforce visibility.
                </p>
              </div>
            </div>

            {error && <div className="dashboard-alert">{error}</div>}

            <div className="dashboard-grid">
              <article className="dashboard-card business-card">
                <div className="dashboard-card-header">
                  <div className="dashboard-card-title-wrap">
                    <span className="dashboard-card-icon business">
                      <BriefcaseBusiness size={20} />
                    </span>
                    <div>
                      <h2>Business Summary</h2>
                      <p>Current month vs previous month business.</p>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <div className="dashboard-card-loading">Loading business summary...</div>
                ) : !businessSummary ? (
                  <div className="dashboard-card-empty">No business summary available.</div>
                ) : (
                  <div className="business-summary-layout">
                    {[businessSummary.currentMonth, businessSummary.previousMonth].map((month) => (
                      <div key={month.label} className="summary-panel">
                        <div className="summary-panel-head">
                          <h3>{month.label}</h3>
                          <span>{formatNumber(month.policyCount)} policies</span>
                        </div>
                        <div className="summary-stat-grid">
                          {isAdmin && (
                            <div className="summary-stat">
                              <span>Gross Premium</span>
                              <strong>{formatCurrency(month.grossPremium)}</strong>
                            </div>
                          )}
                          {isAdmin && (
                            <div className="summary-stat">
                              <span>Net Premium</span>
                              <strong>{formatCurrency(month.netPremium)}</strong>
                            </div>
                          )}
                          <div className="summary-stat">
                            <span>Brokerage</span>
                            <strong>{formatCurrency(month.brokerage)}</strong>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </article>

              {showRenewalCard && (
                <article
                  className={`dashboard-card renewal-card ${
                    renewalExpanded ? 'dashboard-card-expanded' : ''
                  }`}
                >
                  <div className="dashboard-card-header">
                    <div className="dashboard-card-title-wrap">
                      <span className="dashboard-card-icon renewal">
                        <RefreshCcw size={20} />
                      </span>
                      <div>
                        <h2>Renewal & Renewal Summary</h2>
                        <p>Upcoming renewals and quick follow-up details.</p>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="dashboard-expand-btn"
                      onClick={() => setRenewalExpanded((current) => !current)}
                    >
                      {renewalExpanded ? (
                        <>
                          Collapse <ChevronUp size={18} />
                        </>
                      ) : (
                        <>
                          Expand <ChevronDown size={18} />
                        </>
                      )}
                    </button>
                  </div>

                  {loading ? (
                    <div className="dashboard-card-loading">Loading renewal summary...</div>
                  ) : !renewalSummary ? (
                    <div className="dashboard-card-empty">Renewal summary is unavailable.</div>
                  ) : (
                    <>
                      <div className="compact-stat-row">
                        <div className="compact-stat">
                          <span>Renewals</span>
                          <strong>{formatNumber(renewalSummary.totalCount)}</strong>
                        </div>
                        <div className="compact-stat">
                          <span>Retail</span>
                          <strong>{formatNumber(renewalSummary.retailCount)}</strong>
                        </div>
                        <div className="compact-stat">
                          <span>Corporate</span>
                          <strong>{formatNumber(renewalSummary.corporateCount)}</strong>
                        </div>
                        <div className="compact-stat">
                          <span>Premium at Risk</span>
                          <strong>{formatCurrency(renewalSummary.totalGrossPremium)}</strong>
                        </div>
                      </div>

                      <div className={`renewal-details ${renewalExpanded ? 'open' : ''}`}>
                        <div className="renewal-details-head">
                          <h3>Renewal Details</h3>
                          <span>{renewalSummary.windowLabel}</span>
                        </div>

                        <div className="renewal-table-wrap">
                          <table className="renewal-table">
                            <thead>
                              <tr>
                                <th>Client</th>
                                <th>Contact</th>
                                <th>Policy</th>
                                <th>Insurer</th>
                                <th>Renewal Date</th>
                                <th>Premium</th>
                              </tr>
                            </thead>
                            <tbody>
                              {renewalSummary.details?.length ? (
                                renewalSummary.details.map((item) => (
                                  <tr key={item._id}>
                                    <td>
                                      <strong>{item.clientName}</strong>
                                      <span>{item.clientType}</span>
                                    </td>
                                    <td>
                                      <strong>{item.contactPerson || '-'}</strong>
                                      <span>{item.phoneNumber || '-'}</span>
                                      <span>{item.email || '-'}</span>
                                    </td>
                                    <td>
                                      <strong>{item.policyNumber}</strong>
                                      <span>{item.policyName || '-'}</span>
                                      <span>{item.classOfPolicyName || '-'}</span>
                                    </td>
                                    <td>
                                      <strong>{item.insurerName || '-'}</strong>
                                      <span>{item.branchName || '-'}</span>
                                      <span>
                                        {[item.city, item.state].filter(Boolean).join(', ') || '-'}
                                      </span>
                                    </td>
                                    <td>{formatDate(item.policyEndDate)}</td>
                                    <td>{formatCurrency(item.grossPremium)}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="6" className="renewal-empty">
                                    No renewals due in the next 30 days.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </article>
              )}

              {showClaimsCard && (
                <article className="dashboard-card claims-card">
                  <div className="dashboard-card-header">
                    <div className="dashboard-card-title-wrap">
                      <span className="dashboard-card-icon claims">
                        <ClipboardCheck size={20} />
                      </span>
                      <div>
                        <h2>Claims Summary</h2>
                        <p>Claims progress and settlement status.</p>
                      </div>
                    </div>
                  </div>

                  {loading ? (
                    <div className="dashboard-card-loading">Loading claims summary...</div>
                  ) : !claimsSummary ? (
                    <div className="dashboard-card-empty">Claims summary is unavailable.</div>
                  ) : (
                    <div className="compact-stat-row">
                      <div className="compact-stat">
                        <span>Total Claims</span>
                        <strong>{formatNumber(claimsSummary.totalCount)}</strong>
                      </div>
                      <div className="compact-stat">
                        <span>Intimated</span>
                        <strong>{formatNumber(claimsSummary.intimated)}</strong>
                      </div>
                      <div className="compact-stat">
                        <span>Surveyor Assigned</span>
                        <strong>{formatNumber(claimsSummary.surveyorAssigned)}</strong>
                      </div>
                      <div className="compact-stat">
                        <span>Settled</span>
                        <strong>{formatNumber(claimsSummary.settled)}</strong>
                      </div>
                    </div>
                  )}
                </article>
              )}

              {showEmployeeCard && (
                <article className="dashboard-card employee-card">
                  <div className="dashboard-card-header">
                    <div className="dashboard-card-title-wrap">
                      <span className="dashboard-card-icon employees">
                        <Users size={20} />
                      </span>
                      <div>
                        <h2>Employees Summary</h2>
                        <p>Admin-only team visibility and status mix.</p>
                      </div>
                    </div>
                  </div>

                  {loading ? (
                    <div className="dashboard-card-loading">Loading employee summary...</div>
                  ) : !employeeSummary ? (
                    <div className="dashboard-card-empty">Employee summary is unavailable.</div>
                  ) : (
                    <div className="compact-stat-row">
                      <div className="compact-stat">
                        <span>Total Employees</span>
                        <strong>{formatNumber(employeeSummary.totalEmployees)}</strong>
                      </div>
                      <div className="compact-stat">
                        <span>Active</span>
                        <strong>{formatNumber(employeeSummary.activeEmployees)}</strong>
                      </div>
                      <div className="compact-stat">
                        <span>Inactive</span>
                        <strong>{formatNumber(employeeSummary.inactiveEmployees)}</strong>
                      </div>
                      <div className="compact-stat">
                        <span>Admins</span>
                        <strong>{formatNumber(employeeSummary.adminCount)}</strong>
                      </div>
                      <div className="compact-stat">
                        <span>Users</span>
                        <strong>{formatNumber(employeeSummary.userCount)}</strong>
                      </div>
                    </div>
                  )}
                </article>
              )}

              {!showRenewalCard && !showClaimsCard && !showEmployeeCard && !loading && (
                <article className="dashboard-card empty-dashboard-card">
                  <div className="dashboard-card-title-wrap">
                    <span className="dashboard-card-icon neutral">
                      <ShieldCheck size={20} />
                    </span>
                    <div>
                      <h2>More widgets unlock by privilege</h2>
                      <p>
                        This dashboard is showing the modules available to your current role and privileges.
                      </p>
                    </div>
                  </div>
                </article>
              )}

              {loading && (
                <article className="dashboard-card empty-dashboard-card">
                  <div className="dashboard-card-title-wrap">
                    <span className="dashboard-card-icon neutral">
                      <BadgeIndianRupee size={20} />
                    </span>
                    <div>
                      <h2>Loading dashboard widgets</h2>
                      <p>Fetching your personalized dashboard data.</p>
                    </div>
                  </div>
                </article>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
